import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { toast } from "sonner";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Sparkles, Film, CreditCard, Calendar, TrendingUp, BarChart2
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, BarChart, Bar, CartesianGrid, Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_COLORS = {
  "TikTok": "#25f4ee",
  "Instagram Reels": "#e1306c",
  "YouTube Shorts": "#ff0000",
  "Snapchat": "#fffc00",
  "Facebook": "#1877f2",
};

const getPlatformColor = (name) =>
  PLATFORM_COLORS[name] ||
  Object.entries(PLATFORM_COLORS).find(([k]) => k.toLowerCase() === name?.toLowerCase())?.[1] ||
  "#9333ea";

// One distinct color per tone slot — spread across the spectrum
const TONE_COLORS = ["#f87171", "#fbbf24", "#86efac", "#94a3b8", "#60a5fa", "#c084fc"];

const TOOLTIP_CONTENT_STYLE = { background: "#1c1c1c", border: "1px solid #333", borderRadius: 10, fontSize: 12 };
const TOOLTIP_LABEL_STYLE = { color: "#a0a0a0", marginBottom: 2 };
const TOOLTIP_ITEM_STYLE = { color: "#ffffff" };
const CHART_TOOLTIP_STYLE = {
  contentStyle: TOOLTIP_CONTENT_STYLE,
  labelStyle: TOOLTIP_LABEL_STYLE,
  itemStyle: TOOLTIP_ITEM_STYLE,
  cursor: { fill: "rgba(147,51,234,0.06)" },
};

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-5 flex items-center gap-4"
      style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${accent}18`, border: `1px solid ${accent}30` }}>
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </div>
      <div>
        <p className="text-2xl font-black text-white" style={{ fontFamily: "Epilogue, sans-serif" }}>{value}</p>
        <p className="text-xs mt-0.5" style={{ color: "#6b6b6b" }}>{label}</p>
      </div>
    </motion.div>
  );
}

function ChartCard({ title, children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-5" style={{ background: "#1c1c1c", border: "1px solid #2a2a2a" }}>
      <p className="text-sm font-bold text-white mb-5">{title}</p>
      {children}
    </motion.div>
  );
}

const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, value }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5 + 18;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return value > 0 ? (
    <text x={x} y={y} fill="#a0a0a0" textAnchor={x > cx ? "start" : "end"} dominantBaseline="central" fontSize={11}>{name}</text>
  ) : null;
};

export default function Analytics() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/analytics`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => toast.error(t("analytics.loadFailed")))
      .finally(() => setLoading(false));
  }, [t]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
        </div>
      </Layout>
    );
  }

  const { summary, timeline, platforms, tones } = data || {};

  const stats = [
    { label: t("analytics.stats.totalProjects"), value: summary?.total_projects ?? 0, icon: TrendingUp, accent: "#c084fc" },
    { label: t("analytics.stats.adsGenerated"), value: summary?.total_ads ?? 0, icon: Sparkles, accent: "#60a5fa" },
    { label: t("analytics.stats.completedVideos"), value: summary?.completed_ads ?? 0, icon: Film, accent: "#86efac" },
    { label: t("analytics.stats.creditsRemaining"), value: summary?.credits_remaining ?? 0, icon: CreditCard, accent: "#fbbf24" },
    { label: t("analytics.stats.scheduledPosts"), value: summary?.pending_posts ?? 0, icon: Calendar, accent: "#f87171" },
    { label: t("analytics.stats.postsCompleted"), value: summary?.posted_count ?? 0, icon: BarChart2, accent: "#34d399" },
  ];

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="text-3xl font-black text-white" style={{ fontFamily: "Epilogue, sans-serif" }}>{t("analytics.title")}</h1>
        <p className="text-sm mt-1" style={{ color: "#6b6b6b" }}>{t("analytics.subtitle")}</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {stats.map(s => <StatCard key={s.label} {...s} />)}
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* 30-day timeline */}
        <ChartCard title={t("analytics.charts.timeline")}>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="#252525" />
              <XAxis dataKey="date"
                tickFormatter={d => { const dt = new Date(d); return `${dt.getMonth()+1}/${dt.getDate()}`; }}
                tick={{ fill: "#6b6b6b", fontSize: 10 }} axisLine={false} tickLine={false} interval={6} />
              <YAxis tick={{ fill: "#6b6b6b", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} width={20} />
              <Tooltip {...CHART_TOOLTIP_STYLE}
                labelFormatter={d => { const dt = new Date(d); return `${dt.toLocaleDateString(i18n.language,{month:"short",day:"numeric"})}`; }} />
              <Line type="monotone" dataKey="projects" name={t("analytics.charts.projectsSeries")} stroke="#9333ea" strokeWidth={2.5}
                dot={false} activeDot={{ r: 4, fill: "#c084fc" }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Tone distribution */}
        <ChartCard title={t("analytics.charts.tones")}>
          {tones && tones.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={tones} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#252525" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#6b6b6b", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fill: "#a0a0a0", fontSize: 11 }} axisLine={false} tickLine={false} width={80} />
                <Tooltip {...CHART_TOOLTIP_STYLE} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {tones.map((tone, i) => <Cell key={tone.name || `tone-${i}`} fill={TONE_COLORS[i % TONE_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-sm py-8" style={{ color: "#6b6b6b" }}>{t("analytics.noData")}</p>
          )}
        </ChartCard>
      </div>

      {/* Platform breakdown */}
      <ChartCard title={t("analytics.charts.platforms")}>
        {platforms && platforms.length > 0 ? (
          <div className="flex items-center gap-8">
            <ResponsiveContainer width={240} height={200}>
              <PieChart>
                <Pie data={platforms} cx="50%" cy="50%" outerRadius={80} dataKey="value"
                  labelLine={false} label={CustomPieLabel}>
                  {platforms.map((entry) => (
                    <Cell key={entry.name} fill={getPlatformColor(entry.name)} />
                  ))}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-col gap-2">
              {platforms.map(p => (
                <div key={p.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: getPlatformColor(p.name) }} />
                  <span className="text-sm" style={{ color: "#a0a0a0" }}>{p.name}</span>
                  <span className="text-sm font-bold text-white ml-auto pl-4">{p.value}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-center text-sm py-8" style={{ color: "#6b6b6b" }}>{t("analytics.noPlatformData")}</p>
        )}
      </ChartCard>
    </Layout>
  );
}
